import { HeatGrid } from '../charts/HeatGrid.js'
import { LineChart, type LineSeries } from '../charts/LineChart.js'
import { INK, MARINE, SEQUENTIAL, SERIES } from '../charts/chartColors.js'
import { SHIFT_TYPES, SHIFT_TYPE_LABELS, WEEKDAYS_MONDAY_FIRST, callOutGrid, latestByFacility, rateByPeriod } from '../insightsSeries.js'
import { monthLabel } from '../labels.js'
import type { FacilityMonthRollup, PortfolioRow } from '../types.js'

const one = (value: number): string => value.toFixed(1)

/**
 * Attendance (plan section 6.4): call-outs, no-shows and late arrivals per 100 scheduled shifts by month, the chosen
 * facilities against the portfolio; when the call-outs fall, by weekday and shift type; and charting completion per
 * facility in the latest month.
 */
export function AttendanceScreen({ rollups, periods, chosen, rows }: { rollups: readonly FacilityMonthRollup[]; periods: readonly string[]; chosen: readonly PortfolioRow[]; rows: readonly PortfolioRow[] }) {
  const colorOf = (facilityId: string): string => SERIES[chosen.findIndex((row) => row.facilityId === facilityId)] ?? INK
  const perFacility = (row: PortfolioRow) => rollups.filter((rollup) => rollup.facilityId === row.facilityId)
  const callOuts: LineSeries[] = [
    { key: 'portfolio', name: 'Portfolio', color: INK, dashed: true, values: rateByPeriod(rollups, periods, (rollup) => rollup.outcomes.callOuts, (rollup) => rollup.outcomes.scheduled) },
    ...chosen.map((row) => ({ key: row.facilityId, name: row.name, color: colorOf(row.facilityId), values: rateByPeriod(perFacility(row), periods, (rollup) => rollup.outcomes.callOuts, (rollup) => rollup.outcomes.scheduled) })),
  ]
  const lateAndNoShow: LineSeries[] = [
    { key: 'late', name: 'Late arrivals', color: MARINE, values: rateByPeriod(rollups, periods, (rollup) => rollup.outcomes.late, (rollup) => rollup.outcomes.completed) },
    { key: 'noShow', name: 'No-shows', color: '#8E7320', values: rateByPeriod(rollups, periods, (rollup) => rollup.outcomes.noShows, (rollup) => rollup.outcomes.scheduled) },
  ]
  const grid = callOutGrid(rollups)
  const latest = latestByFacility(rollups)
  const charting = rows
    .map((row) => {
      const rollup = latest.get(row.facilityId)
      const due = rollup?.outcomes.chartsDue ?? 0
      return { row, pct: rollup && due > 0 ? Math.round((rollup.outcomes.chartsCompleted / due) * 100) : null, period: rollup?.period ?? null }
    })
    .sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1))
  const scheduled = rollups.reduce((sum, rollup) => sum + rollup.outcomes.scheduled, 0)

  if (scheduled === 0) {
    return <div className="rounded-lg border border-marine/15 bg-white px-4 py-8 text-center text-xs text-ink-mid">No shifts have been reported in this span.</div>
  }
  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="flex-1 text-sm font-black text-ink">Call-outs per 100 scheduled shifts</h2>
          <Legend series={callOuts} />
        </div>
        <LineChart periods={periods} series={callOuts} format={one} periodLabel={monthLabel} title="Call-outs per 100 scheduled shifts by month" height={260} />
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="flex-1 text-sm font-black text-ink">When call-outs happen</h2>
            <span className="text-[11px] text-ink-mid">Count by weekday and shift type · the span · every facility shown</span>
          </div>
          <HeatGrid rows={SHIFT_TYPES.map((type) => SHIFT_TYPE_LABELS[type])} columns={WEEKDAYS_MONDAY_FIRST} values={grid} ramp={['#E8F5EE', '#FBF5E0', '#F2D99A', '#F7A58F', '#D9553F']} format={(value) => String(value)} title="Call-outs by weekday and shift type" />
          <div className="flex items-center gap-2 text-[10px] text-ink-mid">
            <span>Fewer</span>
            <span className="inline-flex gap-0.5">
              {['#E8F5EE', '#FBF5E0', '#F2D99A', '#F7A58F', '#D9553F'].map((color) => (
                <span key={color} className="inline-block h-2.5 w-4" style={{ background: color }} />
              ))}
            </span>
            <span>More</span>
            <span className="ml-auto">The pattern says what the count does not</span>
          </div>
        </section>
        <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="flex-1 text-sm font-black text-ink">No-shows and late arrivals</h2>
            <span className="text-[11px] text-ink-mid">Per 100 shifts, the portfolio</span>
          </div>
          <LineChart periods={periods} series={lateAndNoShow} format={one} periodLabel={monthLabel} title="No-shows and late arrivals per 100 shifts by month" height={220} />
          <Legend series={lateAndNoShow} />
        </section>
      </div>

      <section className="flex flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="flex-1 text-sm font-black text-ink">Charting completion</h2>
          <span className="text-[11px] text-ink-mid">Charts completed over charts due, among the shifts that said either way, each facility's latest month</span>
        </div>
        <ul className="grid grid-cols-3 gap-2 md:grid-cols-6 lg:grid-cols-8">
          {charting.map(({ row, pct, period }) => (
            <li key={row.facilityId} className="flex flex-col gap-1 text-center">
              <div className="relative h-14 rounded bg-marine-lt/40" role="img" aria-label={`${row.name} ${pct === null ? 'no charts reported' : `${pct}% charted`}`}>
                {pct !== null && <div className="absolute inset-x-0 bottom-0 rounded" style={{ height: `${pct}%`, background: pct >= 90 ? SEQUENTIAL[1] : '#F2D99A' }} />}
                <div className="absolute inset-0 flex items-center justify-center text-sm font-black text-ink">{pct === null ? '—' : `${pct}%`}</div>
              </div>
              <span className="truncate text-[10px] text-ink-mid" title={row.name}>
                {row.name}
              </span>
              <span className="text-[10px] text-ink-mid">{pct === null ? 'no charts reported' : period ? monthLabel(period) : ''}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

function Legend({ series }: { series: readonly LineSeries[] }) {
  return (
    <div className="flex flex-wrap gap-3 text-[11px] text-ink-mid">
      {series.map((entry) => (
        <span key={entry.key} className="inline-flex items-center gap-1.5">
          <span className="inline-block h-0 w-3.5" style={{ borderTop: `2px ${entry.dashed ? 'dashed' : 'solid'} ${entry.color}` }} />
          {entry.name}
        </span>
      ))}
    </div>
  )
}
